import React from "react";
import { AbsoluteFill, Easing, Img, interpolate, useCurrentFrame, useVideoConfig } from "remotion";
import { useVideoComponent } from "../components/VideoLayer.js";
import {
  anchoredBoxes,
  anchoredJustify,
  type CardElement,
  type CardMotion,
  type CardMotionType,
  bookendTextDefaults,
  cardMargin,
  cardShadowStyle,
  findBookendFont,
  legacySubtitleColor,
  nearestWeight,
  type BookendScene,
  type TextStyle,
} from "@veyso/core";
import { BackgroundLayer } from "../components/Background.js";
import { easingFor } from "../components/easing.js";
import { useAssetUrl } from "../assets.js";

const FONT = "Inter, -apple-system, Segoe UI, sans-serif";

/**
 * One card line's CSS, from what was set and what was never touched.
 *
 * Nothing here is required, so an untouched card resolves to exactly the fixed
 * values this file used to hard-code. The one deliberate exception is an unset
 * subtitle colour, which keeps its old translucent grey: the panel offers a
 * solid colour, and quietly making every existing subtitle brighter is not a
 * change anyone asked for.
 */
/**
 * One line of a card, resolved.
 *
 * The weight goes through `nearestWeight` because the library is not uniform:
 * Inter carries five weights and a display face carries one, and a browser
 * asked for an 800 that does not exist does not decline — it smears the 400
 * into a synthetic bold that looks nothing like the family. Choosing the
 * nearest real weight here means the editor, the font preloader and the
 * encoder all make the same choice. A stack from before the library is not in
 * it, so it keeps the weight it always had.
 */
export const cardLineStyle = (which: "title" | "subtitle", style: TextStyle | undefined): React.CSSProperties => {
  const fallback = bookendTextDefaults[which];
  const bold = style?.bold ?? fallback.bold;
  const fontFamily = style?.fontFamily ?? fallback.fontFamily;
  const wanted = bold ? fallback.weights.bold : fallback.weights.normal;
  const font = findBookendFont(fontFamily);
  return {
    fontFamily,
    fontSize: style?.fontSize ?? fallback.fontSize,
    fontWeight: font ? nearestWeight(font.weights, wanted) : wanted,
    fontStyle: (style?.italic ?? fallback.italic) ? "italic" : "normal",
    textDecoration: (style?.strikethrough ?? fallback.strikethrough) ? "line-through" : "none",
    color: style?.color ?? (which === "subtitle" ? legacySubtitleColor : fallback.color),
  };
};

/**
 * How one element arrives and leaves.
 *
 * Entries that move stay fully opaque, so the first frame of a card is the
 * card — the rule `cardEntrance` exists to keep, applied per element now that
 * an element can move on its own. A fade and a delay are the two that cannot
 * keep it, and both are called out in the panel rather than discovered on an
 * export.
 *
 * Exits always end invisible. An exit that only moved would slide the element a
 * few pixels and leave it sitting there, which is not leaving.
 *
 * Entry and exit are squeezed proportionally when they would overlap, the same
 * way the focus envelope handles a window too short for both of its ramps: a
 * shortened entrance is a compromise, two ramps fighting over the same frames
 * is a flicker.
 */
export function elementMotion(
  frame: number,
  fps: number,
  durationInFrames: number,
  motion: CardMotion,
): { opacity: number; offsetX: number; offsetY: number } {
  const still = { opacity: 1, offsetX: 0, offsetY: 0 };
  const delay = Math.max(0, Math.round(motion.delay * fps));
  if (frame < delay) return { opacity: 0, offsetX: 0, offsetY: 0 };

  const span = Math.max(1, durationInFrames - delay);
  let entryFrames = motion.entry.type === "none" ? 0 : Math.round(motion.entry.duration * fps);
  let exitFrames = motion.exit.type === "none" ? 0 : Math.round(motion.exit.duration * fps);
  if (entryFrames + exitFrames > span) {
    const squeeze = span / (entryFrames + exitFrames);
    entryFrames = Math.floor(entryFrames * squeeze);
    exitFrames = Math.floor(exitFrames * squeeze);
  }

  const local = frame - delay;
  const exitStart = span - exitFrames;

  if (exitFrames > 0 && local >= exitStart) {
    // `span - 1`, not `span`: the last frame drawn is the one before the end,
    // and an exit that completes a frame later leaves a visible remnant on it.
    const progress = interpolate(local, [exitStart, Math.max(exitStart + 1, span - 1)], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: Easing.in(Easing.quad),
    });
    return leave(motion.exit.type, progress);
  }

  if (entryFrames > 0 && local < entryFrames) {
    const progress = interpolate(local, [0, entryFrames], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing: Easing.out(Easing.cubic),
    });
    return arrive(motion.entry.type, progress);
  }

  return still;
}

/** Composition pixels an element travels. Small: this is emphasis, not transit. */
const TRAVEL = 24;

function arrive(type: CardMotionType, progress: number) {
  const away = 1 - progress;
  if (type === "fade") return { opacity: Number(progress.toFixed(4)), offsetX: 0, offsetY: 0 };
  if (type === "rise") return { opacity: 1, offsetX: 0, offsetY: Number((away * TRAVEL).toFixed(3)) };
  if (type === "slide") return { opacity: 1, offsetX: Number((away * -TRAVEL * 1.6).toFixed(3)), offsetY: 0 };
  return { opacity: 1, offsetX: 0, offsetY: 0 };
}

function leave(type: CardMotionType, progress: number) {
  const opacity = Number((1 - progress).toFixed(4));
  if (type === "rise") return { opacity, offsetX: 0, offsetY: Number((progress * -TRAVEL).toFixed(3)) };
  if (type === "slide") return { opacity, offsetX: Number((progress * TRAVEL * 1.6).toFixed(3)), offsetY: 0 };
  return { opacity, offsetX: 0, offsetY: 0 };
}

/**
 * How a card arrives.
 *
 * One choice, not two sliders. It was a hold and a fade, on the theory that
 * they governed different properties and so could not interfere — and they
 * interfered immediately: raise both and the fade decided everything, because
 * a card at zero opacity is invisible however still it is being held. Worse,
 * choosing a fade quietly reinstated the bug the hold existed to prevent, an
 * export whose first frame is an empty rectangle.
 *
 * So exactly one thing animates how a card appears:
 *
 * - `settle` moves it into place. The first frame is the finished card, which
 *   is what a player shows as the thumbnail, so this is the default.
 * - `fade` brings it up from the background. The first frame *is* empty — that
 *   is what a fade means — and the panel says so rather than leaving it to be
 *   discovered on an export.
 * - `none` is static from the first frame.
 *
 * A `hold` sat beside this, meant to keep the card still so that an early frame
 * was the finished card. Measured, it did the opposite of that in both cases it
 * applied to: it froze a settling card mid-entrance for its whole length, and it
 * pushed a fading card's blank window later by exactly its own duration —
 * lengthening the gap it existed to close. Staggering is a per-element `delay`
 * now, which is what was wanted: a heading, then the line under it.
 */
export function cardEntrance(
  frame: number,
  fps: number,
  entrance: "settle" | "fade" | "none",
): { opacity: number; offsetY: number } {
  if (entrance === "none") return { opacity: 1, offsetY: 0 };

  const progress = interpolate(frame, [0, Math.round(fps * 0.7)], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easingFor("ease-out"),
  });

  return entrance === "fade"
    ? { opacity: progress, offsetY: 0 }
    : { opacity: 1, offsetY: Number(((1 - progress) * 24).toFixed(3)) };
}

/**
 * What one element actually draws.
 *
 * Split from the box so that positioning, motion and paint order are decided in
 * one place for every kind, and only the ink differs here. A line and an arrow
 * are SVG because a one-pixel diagonal is not a div; everything else is a box
 * with a background, which is what the browser is already good at.
 */
const CardElementBody: React.FC<{ element: CardElement }> = ({ element }) => {
  const assetUrl = useAssetUrl();
  // One stored shape, three properties. Which one is decided by the element's
  // kind and its fit, never by anything in the panel.
  const shadow = cardShadowStyle(element);

  if (element.kind === "text") {
    return (
      <p
        style={{
          margin: 0,
          width: "100%",
          lineHeight: element.role === "title" ? 1.05 : 1.3,
          textAlign: element.align,
          textWrap: "balance",
          ...cardLineStyle(element.role, element.style),
          ...shadow,
        }}
      >
        {element.text}
      </p>
    );
  }

  if (element.kind === "image") {
    const src = assetUrl(element.assetKey);
    if (!src) return null;
    return (
      <Img
        src={src}
        style={{
          width: "100%",
          height: "100%",
          objectFit: element.fit,
          borderRadius: element.radius,
          opacity: element.opacity,
          ...shadow,
        }}
      />
    );
  }

  if (element.shape === "rect" || element.shape === "ellipse") {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          background: element.fill,
          opacity: element.opacity,
          borderRadius: element.shape === "ellipse" ? "50%" : element.radius,
          border: element.strokeWidth > 0 ? `${element.strokeWidth}px solid ${element.stroke}` : undefined,
          boxSizing: "border-box",
          ...shadow,
        }}
      />
    );
  }

  // A line is drawn across the middle of its box, so resizing the box is how it
  // is aimed — the same gesture as every other element rather than a second one.
  //
  // Divs rather than SVG. An SVG stretched to a wide, short box scales its
  // contents unevenly: the stroke survives that with `non-scaling-stroke`, but
  // an arrowhead comes out as a sliver, because a polygon has no such escape.
  // Thickness is in composition pixels, like a font size, so it holds at 4K.
  const thickness = Math.max(1, element.strokeWidth);
  const head = thickness * 3;

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", opacity: element.opacity, ...shadow }}>
      <div
        style={{
          position: "absolute",
          left: 0,
          right: element.shape === "arrow" ? head : 0,
          top: "50%",
          height: thickness,
          marginTop: -thickness / 2,
          borderRadius: thickness / 2,
          background: element.stroke,
        }}
      />
      {element.shape === "arrow" ? (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: "50%",
            marginTop: -head * 0.8,
            width: 0,
            height: 0,
            borderTop: `${head * 0.8}px solid transparent`,
            borderBottom: `${head * 0.8}px solid transparent`,
            borderLeft: `${head}px solid ${element.stroke}`,
          }}
        />
      ) : null}
    </div>
  );
};

/** HEAD and END share one renderer: they differ only in content and position. */
export const BookendSection: React.FC<{ scene: BookendScene }> = ({ scene }) => {
  const assetUrl = useAssetUrl();
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const VideoLayer = useVideoComponent();

  // The retired image/text kinds have no elements to carry motion, so they
  // keep the settle they always had. A card takes its movement per element.
  const entrance = cardEntrance(frame, fps, "settle");
  // The section's own length, which is what an element's exit is measured
  // against — not the composition's, which is the whole video.
  const durationInFrames = Math.max(1, Math.round(scene.duration * fps));
  // The retired text kind's own animations. Their blankness at the start is
  // deliberate — a typewriter that begins fully typed is not a typewriter — and
  // is left alone.
  const rise = interpolate(frame, [0, Math.round(fps * 0.7)], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easingFor("ease-out"),
  });

  const content = scene.content;
  // Card-wide, not per element: two elements pinned to the same point stack
  // around it, and no element can know what else is pinned there.
  const boxes = content.kind === "card" ? anchoredBoxes(content.elements) : [];

  if (content.kind === "video") {
    return (
      <AbsoluteFill style={{ backgroundColor: "#000" }}>
        <VideoLayer
          src={assetUrl(content.assetKey)!}
          muted={content.muted}
          trimBefore={content.trimStart > 0 ? Math.round(content.trimStart * fps) : undefined}
          style={{ width: "100%", height: "100%", objectFit: content.fit }}
        />
      </AbsoluteFill>
    );
  }

  if (content.kind === "image") {
    const zoom = content.kenBurns ? 1 + 0.06 * (frame / Math.max(1, fps * 6)) : 1;
    const src = assetUrl(content.assetKey);
    // No picture chosen yet. The card's own background is a truthful stand-in;
    // a broken <Img> would read as a failed render rather than an empty slot.
    if (!src) return <BackgroundLayer background={scene.background} />;
    return (
      <AbsoluteFill style={{ backgroundColor: "#000", overflow: "hidden" }}>
        <Img src={src} style={{ width: "100%", height: "100%", objectFit: content.fit, transform: `scale(${zoom})` }} />
      </AbsoluteFill>
    );
  }

  if (content.kind === "text") {
    const words = content.text.split(/\s+/).filter(Boolean);
    return (
      <AbsoluteFill>
        <BackgroundLayer background={scene.background} />
        <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", padding: "8%" }}>
          <p
            style={{
              margin: 0,
              color: "#f8fafc",
              fontFamily: FONT,
              fontSize: 72,
              fontWeight: 700,
              textAlign: "center",
              lineHeight: 1.15,
              textWrap: "balance",
              ...(content.animation === "fade" ? { opacity: rise } : {}),
              ...(content.animation === "rise" ? { opacity: rise, transform: `translateY(${(1 - rise) * 28}px)` } : {}),
            }}
          >
            {content.animation === "word-by-word"
              ? words.map((word, index) => {
                  // Each word arrives a beat after the last.
                  const at = interpolate(frame, [index * 4, index * 4 + 10], [0, 1], {
                    extrapolateLeft: "clamp",
                    extrapolateRight: "clamp",
                  });
                  return (
                    <span key={index} style={{ opacity: at, marginRight: "0.28em", display: "inline-block" }}>
                      {word}
                    </span>
                  );
                })
              : content.animation === "typewriter"
                ? content.text.slice(0, Math.round(content.text.length * rise))
                : content.text}
          </p>
        </AbsoluteFill>
      </AbsoluteFill>
    );
  }

  return (
    <AbsoluteFill>
      <BackgroundLayer background={scene.background} />
      <AbsoluteFill>
        {content.logoKey ? (
          <Img
            src={assetUrl(content.logoKey)!}
            style={{
              position: "absolute",
              opacity: entrance.opacity,
              transform: `translate(-50%, ${entrance.offsetY}px)`,
              left: "50%",
              top: `${cardMargin * 100}%`,
              height: 72,
              objectFit: "contain",
            }}
          />
        ) : null}
        {content.elements.map((element, index) => {
          const box = boxes[index];
          // An element with no motion of its own rides the card's entrance;
          // one with motion overrides it entirely, so the two never compound
          // into a movement neither of them describes.
          const move = elementMotion(frame, fps, durationInFrames, element.motion);

          return (
            <div
              key={element.id}
              style={{
                position: "absolute",
                left: `${box.x * 100}%`,
                top: `${box.y * 100}%`,
                width: `${box.width * 100}%`,
                height: `${box.height * 100}%`,
                opacity: move.opacity,
                transform: `translate(${move.offsetX}px, ${move.offsetY}px)`,
                display: "flex",
                flexDirection: "column",
                justifyContent: anchoredJustify(element),
                alignItems:
                  element.kind !== "text"
                    ? "stretch"
                    : element.align === "left"
                      ? "flex-start"
                      : element.align === "right"
                        ? "flex-end"
                        : "center",
              }}
            >
              <CardElementBody element={element} />
            </div>
          );
        })}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};
